// Where /login sends an operator once they authenticate.
//
// The value arrives in the URL (`/login?redirect=…`), so it is attacker-
// controllable in the general case even though _app.tsx is what normally
// writes it. Two things have to hold:
//
//   * a deep link must survive login — an admin clicking "Open dispute"
//     in a dispute-filed email is usually signed out, and landing on the
//     dashboard means hunting through the queue for the dispute the
//     email already named (SM-367);
//   * the destination must stay inside this app, so a crafted link
//     cannot bounce a freshly authenticated operator to another origin.
//
// Those are the same requirement stated twice: honour the path, never
// the origin.

/** Where an authenticated operator lands with no deep link to honour. */
export const DEFAULT_AFTER_LOGIN = "/";

/**
 * Narrow an untrusted `?redirect=` value to a same-origin path.
 *
 * Returns null for anything that could leave this origin, so callers can
 * fall back to DEFAULT_AFTER_LOGIN. Rejected rather than rewritten: a
 * value we cannot interpret exactly is not one to guess at.
 */
export function sanitizeRedirect(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value) return null;

  // Must be a rooted path. This also rejects "https://evil.example.com"
  // and "javascript:…", neither of which starts with "/".
  if (!value.startsWith("/")) return null;

  // "//evil.example.com" is protocol-relative — a full origin wearing a
  // path's clothes. "/\evil.example.com" is the same trick, since
  // browsers normalize the backslash to a slash.
  if (value.startsWith("//") || value.startsWith("/\\")) return null;

  // A control character (a raw newline or NUL smuggled through an
  // encoded query) can split the value before it reaches the router.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(value)) return null;

  // Bouncing back to /login would loop, and the loop is silent: the
  // operator submits valid credentials and lands on the login page.
  const path = value.split(/[?#]/, 1)[0];
  if (path === "/login") return null;

  return value;
}

/** sanitizeRedirect with the default applied — what callers navigate to. */
export function redirectTarget(raw: unknown): string {
  return sanitizeRedirect(raw) ?? DEFAULT_AFTER_LOGIN;
}
